import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, CheckCircle2, ChefHat, Clock, Flame,
  Lightbulb, Lock, MapPin, Play, Tag, Users, X,
} from "lucide-react";
import Skeleton from "react-loading-skeleton";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { fetchRecipeById } from "../../services/catalog.api";
import { purchaseRecipe } from "../../services/recipe.api";
import { isAuthenticated } from "../../services/auth.api";
import "./index.css";

const fetchRecipeDetails = (id) => fetchRecipeById(id);

const DIFF_COLOR = { easy: "#059669", medium: "#f59e0b", hard: "#ef4444" };

/** Shown in place of the ingredients when a priced recipe hasn't been bought. */
function RecipePaywall({ recipe, buying, onBuy }) {
  return (
    <div style={{ textAlign: "center", padding: "18px 8px", fontFamily: "'Inter', sans-serif" }}>
      <div
        style={{
          width: 48, height: 48, borderRadius: "50%", background: "#eff6ff",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 12px",
        }}
      >
        <Lock size={20} color="#0d6efd" />
      </div>
      <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#111827" }}>
        Unlock the full recipe
      </p>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#6b7280" }}>
        Buy this recipe for ৳{recipe?.finalPrice ?? recipe?.price} to see every
        ingredient and step.
      </p>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
        <button
          onClick={() => onBuy("eps")}
          disabled={buying}
          style={{
            padding: "9px 18px", borderRadius: 9, border: "none",
            background: buying ? "#9ca3af" : "#0d6efd", color: "#fff",
            fontSize: 13, fontWeight: 700, cursor: buying ? "not-allowed" : "pointer",
          }}
        >
          {buying ? "Starting…" : "Pay with bKash / Nagad"}
        </button>
        <button
          onClick={() => onBuy("stripe")}
          disabled={buying}
          style={{
            padding: "9px 18px", borderRadius: 9, border: "1.5px solid #e5e7eb",
            background: "#fff", color: "#374151",
            fontSize: 13, fontWeight: 700, cursor: buying ? "not-allowed" : "pointer",
          }}
        >
          Pay by card
        </button>
      </div>
    </div>
  );
}

export default function RecipeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [videoOpen, setVideoOpen] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [buying, setBuying] = useState(false);

  const { data: recipe, isPending, error } = useQuery({
    queryKey: ["recipe-details", id],
    queryFn: () => fetchRecipeDetails(id),
    enabled: !!id,
  });

  // A priced recipe the visitor has not bought comes back with its ingredients
  // and steps withheld; the API flags that with `isLocked`.
  const handleBuy = async (method) => {
    if (!isAuthenticated()) {
      navigate("/authentication/login");
      return;
    }
    try {
      setBuying(true);
      const result = await purchaseRecipe(id, method);
      if (result?.redirectUrl) {
        window.location.href = result.redirectUrl;
        return;
      }
      toast.error("Could not start the payment. Please try again.");
    } catch (e) {
      toast.error(e?.message || "Could not start the payment.");
    } finally {
      setBuying(false);
    }
  };

  useEffect(() => {
    if (error) toast.error(error?.message || "Failed to load recipe");
  }, [error]);

  const flattenedSteps = useMemo(() => {
    const walk = (items = [], level = 0) =>
      items.flatMap((step, i) => [
        { ...step, level, stepNumber: i + 1 },
        ...walk(step?.subSteps || [], level + 1),
      ]);
    return walk(recipe?.steps || []);
  }, [recipe?.steps]);

  const recipeTags = useMemo(() => {
    const raw = Array.isArray(recipe?.tags) ? recipe.tags
      : Array.isArray(recipe?.tagIds) ? recipe.tagIds : [];
    return raw.map(t =>
      typeof t === "string" ? t.trim()
        : (t?.name || t?.title || t?.label || t?.tag || "").toString().trim()
    ).filter(Boolean);
  }, [recipe?.tags, recipe?.tagIds]);

  const getEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes("youtube.com/embed/")) return url;
    if (url.includes("youtube.com/watch")) {
      const v = new URL(url).searchParams.get("v");
      return v ? `https://www.youtube.com/embed/${v}` : null;
    }
    if (url.includes("youtu.be/")) {
      const vid = url.split("youtu.be/")[1]?.split("?")[0];
      return vid ? `https://www.youtube.com/embed/${vid}` : null;
    }
    return url;
  };

  const videoUrl = getEmbedUrl(recipe?.videoUrl);
  const shouldShowPrice = Boolean(recipe?.isPaid) && Number(recipe?.finalPrice ?? recipe?.price) > 0;
  const diffColor = DIFF_COLOR[recipe?.difficultyLevel?.toLowerCase()] || "#6b7280";

  if (error) return null;

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "none", border: "none", padding: "0 0 16px",
          fontSize: 13, fontWeight: 600, color: "#6b7280",
          fontFamily: "'Inter', sans-serif", cursor: "pointer",
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      {/* ── Top section: hero + sidebar ─────────────── */}
      <div className="rd-top-layout">

        {/* Left: image + meta */}
        <div className="rd-left">
          {/* Hero image */}
          <div style={{ borderRadius: 14, overflow: "hidden", position: "relative", background: "#f1f5f9" }}>
            {isPending ? (
              <Skeleton height={380} />
            ) : (
              <>
                <img
                  src={recipe?.images?.[0] || "/Assets/Img/Recipe/imgHub_1_3.png"}
                  alt={recipe?.title}
                  style={{ width: "100%", height: 380, objectFit: "cover", display: "block" }}
                />
                {/* Overlaid badges */}
                <div style={{ position: "absolute", top: 12, left: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ background: recipe?.isPaid ? "#0d6efd" : "#059669", color: "#fff", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
                    {recipe?.isPaid ? "Paid" : "Free"}
                  </span>
                  {recipe?.difficultyLevel && (
                    <span style={{ background: diffColor, color: "#fff", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, fontFamily: "'Inter', sans-serif", textTransform: "capitalize" }}>
                      {recipe.difficultyLevel}
                    </span>
                  )}
                </div>
                {/* Thumbnail strip */}
                {recipe?.images?.length > 1 && (
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, display: "flex", gap: 4, padding: "8px 10px", background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent)" }}>
                    {recipe.images.slice(1, 5).map((img, i) => (
                      <img key={i} src={img} alt="" onClick={() => setLightboxImg(img)}
                        style={{ width: 52, height: 40, objectFit: "cover", borderRadius: 6, border: "2px solid rgba(255,255,255,0.5)", cursor: "pointer", flexShrink: 0 }}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Title + tags + description */}
          <div style={{ marginTop: 20 }}>
            {isPending ? <Skeleton height={32} width="60%" /> : (
              <h1 style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", fontFamily: "'Inter', sans-serif", margin: "0 0 8px" }}>
                {recipe?.title}
              </h1>
            )}

            {/* Tags */}
            {!isPending && recipeTags.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                <Tag size={13} color="#9ca3af" style={{ marginTop: 3, flexShrink: 0 }} />
                {recipeTags.map((tag, i) => (
                  <span key={i} style={{ background: "#f1f5f9", color: "#374151", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Price */}
            {shouldShowPrice && !isPending && (
              <div style={{ display: "inline-flex", alignItems: "baseline", gap: 6, marginBottom: 12 }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: "#059669", fontFamily: "'Inter', sans-serif" }}>
                  ৳{recipe?.finalPrice ?? recipe?.price}
                </span>
                {!!recipe?.discountValue && recipe?.discountType !== "none" && (
                  <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>
                    {recipe.discountValue}{recipe.discountType === "percentage" ? "% OFF" : " TK OFF"}
                  </span>
                )}
              </div>
            )}

            {/* Description */}
            {!isPending && recipe?.description && (
              <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.7, fontFamily: "'Inter', sans-serif", margin: 0 }}>
                {recipe.description}
              </p>
            )}
            {isPending && <Skeleton count={3} height={16} />}
          </div>
        </div>

        {/* Right: sidebar */}
        <div className="rd-sidebar">

          {/* Quick stats */}
          <div className="rd-card" style={{ marginBottom: 14 }}>
            <p className="rd-section-label">Quick Info</p>
            {isPending ? <Skeleton count={4} height={20} className="mb-2" /> : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { icon: <Clock size={15} color="#0d6efd" />, label: "Cook Time", value: recipe?.cookingTime || "—" },
                  { icon: <Clock size={15} color="#f59e0b" />, label: "Prep Time", value: recipe?.preparationTime || "—" },
                  { icon: <Users size={15} color="#059669" />, label: "Serves",    value: recipe?.serves || "—" },
                  { icon: <Flame size={15} color={diffColor} />, label: "Difficulty", value: recipe?.difficultyLevel || "—" },
                ].map(({ icon, label, value }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: "#6b7280", fontFamily: "'Inter', sans-serif" }}>
                      {icon} {label}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "'Inter', sans-serif", textTransform: "capitalize" }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Watch video */}
            <button
              onClick={() => videoUrl && setVideoOpen(true)}
              disabled={!videoUrl}
              style={{
                width: "100%", marginTop: 16,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "10px", borderRadius: 10, border: "none",
                background: videoUrl ? "#0d6efd" : "#f1f5f9",
                color: videoUrl ? "#fff" : "#9ca3af",
                fontSize: 13, fontWeight: 700, fontFamily: "'Inter', sans-serif",
                cursor: videoUrl ? "pointer" : "not-allowed",
              }}
            >
              <Play size={14} fill={videoUrl ? "#fff" : "#9ca3af"} />
              {videoUrl ? "Watch Video" : "No Video Available"}
            </button>
          </div>

          {/* Kitchen card */}
          <div className="rd-card">
            <p className="rd-section-label">Kitchen</p>
            {isPending ? <Skeleton height={80} /> : (
              <>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <img
                    src={recipe?.kitchenId?.kitchenLogo || "/Assets/Img/kitchen_default.png"}
                    alt={recipe?.kitchenId?.name}
                    style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: "2px solid #f1f5f9", flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "0 0 3px", fontFamily: "'Inter', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {recipe?.kitchenId?.name || "Kitchen"}
                    </p>
                    {recipe?.kitchenId?.address?.text && (
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 4, fontSize: 11, color: "#9ca3af" }}>
                        <MapPin size={11} style={{ marginTop: 1, flexShrink: 0 }} />
                        <span style={{ fontFamily: "'Inter', sans-serif", lineHeight: 1.4 }}>
                          {recipe.kitchenId.address.text}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {recipe?.kitchenId?._id && (
                  <Link
                    to={`/SellerProfile/${recipe.kitchenId._id}`}
                    style={{
                      display: "block", marginTop: 12, textAlign: "center",
                      padding: "8px", borderRadius: 8,
                      border: "1.5px solid #e5e7eb", background: "#f9fafb",
                      fontSize: 12, fontWeight: 600, color: "#374151",
                      textDecoration: "none", fontFamily: "'Inter', sans-serif",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <ChefHat size={13} style={{ marginRight: 5 }} />
                    View Kitchen
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Ingredients + Tips ───────────────────────── */}
      <div className="rd-two-col" style={{ marginTop: 24 }}>

        {/* Ingredients */}
        <div className="rd-card">
          <p className="rd-section-label">Ingredients</p>
          {isPending ? <Skeleton count={6} height={22} className="mb-2" /> :
            recipe?.isLocked ? (
              <RecipePaywall recipe={recipe} buying={buying} onBuy={handleBuy} />
            ) :
            recipe?.ingredients?.length ? (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {recipe.ingredients.map((item, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#374151", fontFamily: "'Inter', sans-serif" }}>
                    <CheckCircle2 size={15} color="#059669" style={{ marginTop: 1, flexShrink: 0 }} />
                    <span>{`${item?.quantity || ""} ${item?.qtyUnit || ""} ${item?.name || ""}`.trim()}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ fontSize: 13, color: "#9ca3af", margin: 0, fontFamily: "'Inter', sans-serif" }}>No ingredients provided.</p>
            )
          }
        </div>

        {/* Tips */}
        <div className="rd-card">
          <p className="rd-section-label">
            <Lightbulb size={14} color="#f59e0b" style={{ marginRight: 5 }} />
            Recipe Tips
          </p>
          {isPending ? <Skeleton count={5} height={18} /> : (
            <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, margin: 0, fontFamily: "'Inter', sans-serif" }}>
              {recipe?.tips || "No tips available for this recipe."}
            </p>
          )}
        </div>
      </div>

      {/* ── Cooking Steps ────────────────────────────── */}
      {(isPending || flattenedSteps.length > 0) && (
        <div className="rd-card" style={{ marginTop: 16 }}>
          <p className="rd-section-label" style={{ fontSize: 16 }}>
            Cooking Steps
            {!isPending && flattenedSteps.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "none", letterSpacing: 0 }}>
                {flattenedSteps.filter(s => s.level === 0).length} steps
              </span>
            )}
          </p>

          {isPending ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[1,2,3,4].map(i => (
                <div key={i} style={{ display: "flex", gap: 12 }}>
                  <Skeleton circle width={28} height={28} />
                  <div style={{ flex: 1 }}><Skeleton height={48} borderRadius={8} /></div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ position: "relative" }}>

              {/* Vertical timeline line */}
              <div style={{
                position: "absolute",
                left: 13,
                top: 28,
                bottom: 14,
                width: 2,
                background: "linear-gradient(to bottom, #0d6efd 60%, #e5e7eb 100%)",
                borderRadius: 2,
                zIndex: 0,
              }} />

              <div style={{ display: "flex", flexDirection: "column" }}>
                {flattenedSteps.map((step, idx) => {
                  const isMain = step.level === 0;
                  const isLast = idx === flattenedSteps.length - 1;
                  return (
                    <div
                      key={step?._id || idx}
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "flex-start",
                        paddingLeft: step.level * 28,
                        paddingBottom: isLast ? 0 : 10,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {/* Circle */}
                      <div style={{
                        width: 28, height: 28,
                        borderRadius: "50%",
                        flexShrink: 0,
                        background: isMain ? "#0d6efd" : "#fff",
                        color: isMain ? "#fff" : "#9ca3af",
                        border: isMain ? "none" : "1.5px solid #d1d5db",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, fontWeight: 800,
                        fontFamily: "'Inter', sans-serif",
                        boxShadow: isMain ? "0 0 0 3px #dbeafe" : "none",
                      }}>
                        {step.stepNumber}
                      </div>

                      {/* Card */}
                      <div style={{
                        flex: 1,
                        background: isMain ? "#fff" : "#fafafa",
                        borderRadius: 8,
                        padding: "8px 12px",
                        border: isMain ? "1px solid #e0edff" : "1px dashed #e5e7eb",
                        minWidth: 0,
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {/* Title row */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: step?.description ? 3 : 0 }}>
                              <span style={{
                                fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
                                textTransform: "uppercase",
                                color: isMain ? "#0d6efd" : "#9ca3af",
                                fontFamily: "'Inter', sans-serif",
                                flexShrink: 0,
                              }}>
                                {isMain ? `Step ${step.stepNumber}` : "Sub"}
                              </span>
                              <span style={{ width: 1, height: 10, background: "#e5e7eb", flexShrink: 0 }} />
                              <p style={{
                                margin: 0, fontSize: 13, fontWeight: 700,
                                color: "#111827", fontFamily: "'Inter', sans-serif",
                                lineHeight: 1.3, overflow: "hidden",
                                whiteSpace: "nowrap", textOverflow: "ellipsis",
                              }}>
                                {step?.title || "Untitled"}
                              </p>
                            </div>

                            {/* Description */}
                            {step?.description && (
                              <p style={{
                                margin: 0, fontSize: 12, color: "#6b7280",
                                lineHeight: 1.55, fontFamily: "'Inter', sans-serif",
                              }}>
                                {step.description}
                              </p>
                            )}
                          </div>

                          {/* Step image */}
                          {step?.image && (
                            <motion.img
                              src={step.image}
                              alt={step.title || "Step"}
                              whileHover={{ scale: 1.05 }}
                              transition={{ type: "spring", stiffness: 300, damping: 20 }}
                              onClick={() => setLightboxImg(step.image)}
                              style={{
                                width: 72, height: 54,
                                objectFit: "cover", borderRadius: 6,
                                cursor: "zoom-in", flexShrink: 0,
                                border: "1px solid #e5e7eb",
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Video modal ──────────────────────────────── */}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setVideoOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={e => e.stopPropagation()}
              style={{ width: "100%", maxWidth: 780, background: "#000", borderRadius: 14, overflow: "hidden", position: "relative" }}
            >
              <button
                onClick={() => setVideoOpen(false)}
                style={{ position: "absolute", top: 10, right: 10, zIndex: 1, background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}
              >
                <X size={16} />
              </button>
              <div style={{ aspectRatio: "16/9" }}>
                <iframe
                  src={videoUrl} title="Recipe Video" frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen style={{ width: "100%", height: "100%" }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Image lightbox ───────────────────────────── */}
      <AnimatePresence>
        {lightboxImg && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightboxImg(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          >
            <button
              onClick={() => setLightboxImg(null)}
              style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImg} alt="Step"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain", borderRadius: 12 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
